import React, { FormEvent, useEffect } from 'react';
import useTranslate from '@src/i18n/useTranslate';
import DotFalling from '@src/components/reusable/spinner/DotFalling';
import { extractValues } from '@src/common/index';
import { RECOVERY_STEP } from '@src/features/customers/types';
import { usePasswordRecovery } from '@src/features/customers/auth/password-recovery/hooks/usePasswordRecovery';
import { useAppDispatch } from '@src/core/hooks';
import { setErrors } from '@src/features/customers/auth/store/auth';
import InputPassword from '@src/features/customers/auth/legacy/inputs/InputPassword';
import {
  AuthContainer,
  AuthForm,
  AuthHeader,
  AuthHeaderTitle,
  AuthHeaderSubtitle,
  AuthButtonGroup,
  AuthPrimaryButton,
  AuthPreviousStepButton,
} from '@src/features/customers/auth/legacy/styled/AuthStyled';
import { track } from '@src/services/analytics';
import { validateRecoveryPassword } from '@src/features/customers/auth/password-recovery/core/validateRecoveryPassword';

interface PasswordRecoveryProps {
  onBack: () => void;
}

const PasswordRecoveryForm = ({ onBack }: PasswordRecoveryProps) => {
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const { changePassword, loading, recoveryEmail: email, recoveryCode: code } = usePasswordRecovery();

  useEffect(() => {
    dispatch(setErrors(RECOVERY_STEP.PASSWORD_RECOVER, {}));
    track('recoveryPasswordRecoveryStepAccess');
  }, []);

  const handleSubmit = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    const values = extractValues(ev.target as HTMLFormElement);

    const validation = validateRecoveryPassword(values.password, values.passwordConfirmation);

    dispatch(setErrors(RECOVERY_STEP.PASSWORD_RECOVER, validation));

    if (Object.keys(validation).length > 0) return;

    track('recoveryPasswordRecoveryStepSubmit');

    changePassword(email ?? '', code ?? '', values.password);
  };

  return (
    <AuthContainer>
      <AuthForm key={RECOVERY_STEP.PASSWORD_RECOVER} onSubmit={handleSubmit}>
        <AuthHeader>
          <AuthHeaderTitle>{i18n('auth_recoverPassword')}</AuthHeaderTitle>
          <AuthHeaderSubtitle>{i18n('auth_passwordRecoverySubtitle')}</AuthHeaderSubtitle>
          <InputPassword
            name='password'
            label={i18n('auth_labelPassword')}
            errorContext={RECOVERY_STEP.PASSWORD_RECOVER}
          />
          <InputPassword
            name='passwordConfirmation'
            label={i18n('auth_passwordConfirmation')}
            errorContext={RECOVERY_STEP.PASSWORD_RECOVER}
          />
          <AuthButtonGroup>
            <AuthPrimaryButton>{loading ? <DotFalling /> : i18n('auth_redefinePassword')}</AuthPrimaryButton>
            <AuthPreviousStepButton onClick={() => onBack()}>{i18n('auth_goBackButton')}</AuthPreviousStepButton>
          </AuthButtonGroup>
        </AuthHeader>
      </AuthForm>
    </AuthContainer>
  );
};

export default PasswordRecoveryForm;
